import { useEffect, useRef, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { AIA_ACTIONS, kc, startAccountAction } from '../auth/keycloak';
import type { CurrentUser } from '../auth/useUser';

// #399 — the account menu in the sidebar's user block, where Sign Out already
// was. Everything that is about YOU rather than about a case hangs off here:
// the profile page, the settings page, the two Keycloak self-service actions,
// and sign-out.
//
// A popover rather than more sidebar entries: these are occasional actions,
// and the nav is already 15 items over four sections. It is a plain
// button + div rather than <dialog> — the shared Dialog is modal
// (showModal()), which is the wrong interaction for "glance at a menu, press
// Escape".

/** The one-word role label the sidebar already showed, kept as the source. */
export function roleLabel(user: CurrentUser): string {
  if (user.canAdmin) return 'ADMIN';
  if (user.canApprove) return 'APPROVER';
  if (user.canEdit) return 'ANALYST';
  if (user.isLegal) return 'LEGAL';
  return '';
}

export function ProfileMenu({ user, onNavigate }: {
  user: CurrentUser;
  onNavigate: (page: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on Escape or on a click outside. Both are registered only while the
  // menu is open, so the shell carries no listeners in its resting state.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const go = (page: string) => { setOpen(false); onNavigate(page); };

  const item: React.CSSProperties = {
    ...styles.btnUnstyled,
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    fontSize: 13,
    color: C.text,
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ ...styles.btnUnstyled, display: 'block', width: '100%' }}
        onClick={() => setOpen(v => !v)}
      >
        <div style={{ color: C.sideText }}>{user.name}</div>
        <div style={{ color: C.sideTextDim, fontSize: 11 }}>
          {roleLabel(user)} <span aria-hidden="true">▾</span>
        </div>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            marginBottom: 8,
            background: C.bgCard,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            padding: 6,
            zIndex: 50,
          }}
        >
          <div style={{ padding: '6px 12px 10px', borderBottom: `1px solid ${C.border}`, marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{user.name}</div>
            <div style={{ fontSize: 11, color: C.textMuted, wordBreak: 'break-all' }}>{user.email}</div>
          </div>

          <button type="button" role="menuitem" style={item} onClick={() => go('profile')}>My profile</button>
          <button type="button" role="menuitem" style={item} onClick={() => go('preferences')}>Settings</button>

          <div style={{ borderTop: `1px solid ${C.border}`, margin: '6px 0' }} />

          {/* Both leave the SPA for Keycloak's login theme and come back to
              this URL — see startAccountAction. Nothing here touches a
              credential. */}
          <button
            type="button"
            role="menuitem"
            style={item}
            onClick={() => startAccountAction(AIA_ACTIONS.changePassword)}
          >
            Change password
          </button>
          <button
            type="button"
            role="menuitem"
            style={item}
            onClick={() => startAccountAction(AIA_ACTIONS.configureTotp)}
          >
            Reconfigure authenticator
          </button>

          <div style={{ borderTop: `1px solid ${C.border}`, margin: '6px 0' }} />

          <button
            type="button"
            role="menuitem"
            style={{ ...item, color: C.danger }}
            onClick={() => kc.logout()}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
